import React from "react";
import { Link } from "react-router-dom";
import Icon from "components/AppIcon";

const STATS = [
  { value: "12,000+", label: "Active Members Managed" },
  { value: "340+", label: "Gyms Onboarded" },
  { value: "99.9%", label: "Platform Uptime" },
  { value: "4.9★", label: "Average Rating" },
];

export default function HeroSection() {
  return (
    <section className="relative pt-32 pb-20 px-4 overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[500px] bg-teal-600/10 rounded-full blur-3xl" />
        <div className="absolute top-20 right-0 w-64 h-64 bg-primary/10 rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-0 w-64 h-64 bg-teal-800/10 rounded-full blur-3xl" />
        {/* Grid pattern */}
        <div className="absolute inset-0 opacity-[0.03]" style={{
          backgroundImage: "linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)",
          backgroundSize: "60px 60px"
        }} />
      </div>
      <div className="relative max-w-7xl mx-auto">
        <div className="text-center max-w-4xl mx-auto">
          {/* Badge */}
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-teal-500/10 border border-teal-500/20 text-teal-400 text-sm font-caption mb-6">
            <span className="w-2 h-2 rounded-full bg-teal-400 animate-pulse" />
            Trusted by 340+ gyms across India
          </div>

          <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold font-heading leading-tight mb-6">
            Run your gym{" "}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-teal-400 to-teal-600">
              smarter
            </span>
            , not harder
          </h1>

          <p className="text-lg sm:text-xl text-slate-400 font-body max-w-2xl mx-auto mb-10 leading-relaxed">
            myfitnesstracking handles member management, attendance tracking, billing, and analytics — so you can focus on building a better gym experience.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-16">
            <Link
              to="/registration-page"
              className="flex items-center gap-2 px-8 py-3.5 rounded-xl bg-primary hover:bg-primary/90 text-white font-semibold font-caption text-base transition-all duration-200 shadow-xl shadow-teal-900/30 hover:shadow-teal-900/50 hover:-translate-y-0.5"
            >
              Start 14-day free trial
              <Icon name="ArrowRight" size={18} />
            </Link>
            <Link
              to="/executive-overview"
              className="flex items-center gap-2 px-8 py-3.5 rounded-xl border border-white/10 hover:border-white/20 text-white font-semibold font-caption text-base transition-all duration-200 hover:bg-white/5"
            >
              <Icon name="Play" size={16} />
              View live demo
            </Link>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-6 max-w-3xl mx-auto">
            {STATS?.map((stat) => (
              <div key={stat?.label} className="text-center">
                <div className="text-2xl sm:text-3xl font-bold text-white font-heading">{stat?.value}</div>
                <div className="text-xs text-slate-500 font-body mt-1">{stat?.label}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Dashboard Preview */}
        <div className="mt-16 relative max-w-5xl mx-auto">
          <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-transparent to-transparent z-10 pointer-events-none" style={{ top: "60%" }} />
          <div className="bg-slate-800/50 border border-white/10 rounded-2xl overflow-hidden shadow-2xl">
            {/* Fake browser bar */}
            <div className="flex items-center gap-2 px-4 py-3 border-b border-white/5 bg-slate-900/50">
              <div className="flex gap-1.5">
                <div className="w-3 h-3 rounded-full bg-red-500/60" />
                <div className="w-3 h-3 rounded-full bg-amber-500/60" />
                <div className="w-3 h-3 rounded-full bg-emerald-500/60" />
              </div>
              <div className="flex-1 mx-4 h-6 rounded-md bg-slate-700/50 flex items-center px-3">
                <span className="text-xs text-slate-500 font-caption">app.fitpulse.in/executive-overview</span>
              </div>
            </div>
            {/* Dashboard mockup */}
            <div className="p-6 grid grid-cols-4 gap-4">
              {["Total Revenue", "Active Members", "Retention Rate", "Monthly Growth"]?.map((label, i) => (
                <div key={label} className="bg-slate-700/40 rounded-xl p-4 border border-white/5">
                  <div className="text-xs text-slate-500 font-caption mb-2">{label}</div>
                  <div className="text-xl font-bold text-white font-heading">
                    {["$76.4K", "1,222", "84.2%", "+8.7%"]?.[i]}
                  </div>
                  <div className={`text-xs font-caption mt-1 ${i === 3 ? "text-red-400" : "text-emerald-400"}`}>
                    {["+12.4%", "+5.8%", "+1.3%", "-0.4%"]?.[i]} vs last month
                  </div>
                </div>
              ))}
              <div className="col-span-3 bg-slate-700/40 rounded-xl p-4 border border-white/5 h-32">
                <div className="text-xs text-slate-500 font-caption mb-3">Revenue & Member Trend</div>
                <div className="flex items-end gap-2 h-16">
                  {[40, 55, 45, 70, 60, 80, 75, 90, 85, 95, 88, 100]?.map((h, i) => (
                    <div key={i} className="flex-1 bg-teal-500/30 rounded-t-sm" style={{ height: `${h}%` }} />
                  ))}
                </div>
              </div>
              <div className="bg-slate-700/40 rounded-xl p-4 border border-white/5 h-32">
                <div className="text-xs text-slate-500 font-caption mb-2">Alerts</div>
                {["3 expiring", "2 pending", "1 overdue"]?.map((a, i) => (
                  <div key={i} className="flex items-center gap-2 mt-2">
                    <div className={`w-1.5 h-1.5 rounded-full ${["bg-amber-400", "bg-red-400", "bg-orange-400"]?.[i]}`} />
                    <span className="text-xs text-slate-400 font-body">{a}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
